import React, { useRef, useState } from 'react';
import { useUi } from '@/context/Ui';
import { useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, Parrot, RoundPlayButton } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { getStepNumber } from '../utils';

interface ListenToWordProps {
  word: string;
}

const ListenToWord = ({ word }: ListenToWordProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const { sendAlert } = useUi();

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  return (
    <>
      <WhitePanel mode="basic" kind="fill" color="paper">
        <div className="flex gap-12 justify-center items-center h-full">
          <Parrot className="block w-96 h-auto" type="pointing" />

          <RoundPlayButton
            className="my-6"
            color="white"
            onClick={() => {
              playerRef.current?.play();
              setPlayedOnce(true);
            }}
            disabled={!playerReady}
            pressHint={!playedOnce}
          />
        </div>
      </WhitePanel>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        pause={500}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </>
  );
};

export const ReadingListening = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  const { words } = activity;

  const NUMBER_OF_STEPS_PER_SOUND = 1;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          total={words.length * NUMBER_OF_STEPS_PER_SOUND + 1}
        />,
        ...words?.flatMap((word, index) => [
          <ActivityStep
            key={`${activity.id}-${index}-1`}
            stepIdentifier={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            stepNumber={getStepNumber(index + 1, 0, NUMBER_OF_STEPS_PER_SOUND)}
            totalSteps={words.length * NUMBER_OF_STEPS_PER_SOUND + 1}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            usePanel
            showTitle={false}
            showActivityPagination={false}
          >
            <ListenToWord word={word} />
          </ActivityStep>,
        ]),
      ]}
    />
  );
};
